import { useEffect, useRef } from "react";

import { useWebSocket } from "@/context/WebSocketContext";
import type { WebSocketMessage } from "@/services/WebSocketManager";
import { WS_CHANNELS } from "@/utils/websocket";

import {
  caseCheckingCaseId,
  type CaseCheckingWsDocument,
  matchesCaseCheckingCompany,
  parseCaseCheckingWsMessage,
} from "./caseCheckingWs";

const DEBOUNCE_MS = 350;

export type CaseCheckingRealtimeHandlers = {
  onCaseListChange?: () => void;
  onCaseChange?: (caseId: string, document: CaseCheckingWsDocument) => void;
  onDocumentsChange?: (caseId: string) => void;
};

type UseCaseCheckingRealtimeOptions = {
  enabled?: boolean;
  companyId?: string | null;
  /** When true, accept WS payloads for any tenant (Ani staff; server filters delivery). */
  skipCompanyFilter?: boolean;
  caseId?: string | null;
} & CaseCheckingRealtimeHandlers;

function scheduleDebounced(
  timers: Map<string, ReturnType<typeof setTimeout>>,
  key: string,
  fn: () => void,
) {
  const existing = timers.get(key);
  if (existing) clearTimeout(existing);
  timers.set(
    key,
    setTimeout(() => {
      timers.delete(key);
      fn();
    }, DEBOUNCE_MS),
  );
}

function handleCaseCheckingMessage(
  message: WebSocketMessage,
  options: {
    companyId?: string | null;
    skipCompanyFilter?: boolean;
    caseId?: string | null;
    handlers: CaseCheckingRealtimeHandlers;
    timers: Map<string, ReturnType<typeof setTimeout>>;
    source: "cases" | "documents";
  },
) {
  const parsed = parseCaseCheckingWsMessage(message);
  const fullDoc = parsed?.full_document;
  const op = parsed?.operation;
  if (!fullDoc || (op !== "insert" && op !== "update" && op !== "delete")) return;
  if (!options.skipCompanyFilter && !matchesCaseCheckingCompany(fullDoc, options.companyId)) return;

  const docCaseId = caseCheckingCaseId(fullDoc);
  if (!docCaseId) return;

  if (options.caseId && docCaseId !== options.caseId) return;

  if (options.source === "documents") {
    scheduleDebounced(options.timers, `docs:${docCaseId}`, () => {
      options.handlers.onDocumentsChange?.(docCaseId);
    });
    return;
  }

  scheduleDebounced(options.timers, `case:${docCaseId}`, () => {
    options.handlers.onCaseChange?.(docCaseId, fullDoc);
    options.handlers.onCaseListChange?.();
  });
}

export function useCaseCheckingRealtime({
  enabled = true,
  companyId,
  skipCompanyFilter = false,
  caseId,
  onCaseListChange,
  onCaseChange,
  onDocumentsChange,
}: UseCaseCheckingRealtimeOptions) {
  const { subscribe, unsubscribe, onMessage } = useWebSocket();
  const handlersRef = useRef<CaseCheckingRealtimeHandlers>({
    onCaseListChange,
    onCaseChange,
    onDocumentsChange,
  });
  handlersRef.current = { onCaseListChange, onCaseChange, onDocumentsChange };

  useEffect(() => {
    if (!enabled || !companyId) return;

    const casesChannel = WS_CHANNELS.CASE_CHECKING_CASES;
    const documentsChannel = WS_CHANNELS.CASE_CHECKING_DOCUMENTS;
    subscribe(casesChannel);
    subscribe(documentsChannel);

    return () => {
      unsubscribe(casesChannel);
      unsubscribe(documentsChannel);
    };
  }, [companyId, enabled, subscribe, unsubscribe]);

  useEffect(() => {
    if (!enabled || !companyId) return;

    const timers = new Map<string, ReturnType<typeof setTimeout>>();
    const casesChannel = WS_CHANNELS.CASE_CHECKING_CASES;
    const documentsChannel = WS_CHANNELS.CASE_CHECKING_DOCUMENTS;

    const offCases = onMessage(casesChannel, (message) => {
      handleCaseCheckingMessage(message, {
        companyId,
        skipCompanyFilter,
        caseId,
        handlers: handlersRef.current,
        timers,
        source: "cases",
      });
    });

    const offDocuments = onMessage(documentsChannel, (message) => {
      handleCaseCheckingMessage(message, {
        companyId,
        skipCompanyFilter,
        caseId,
        handlers: handlersRef.current,
        timers,
        source: "documents",
      });
    });

    return () => {
      offCases();
      offDocuments();
      timers.forEach((timer) => clearTimeout(timer));
      timers.clear();
    };
  }, [caseId, companyId, enabled, onMessage, skipCompanyFilter]);
}
